import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EmailCompositionValue } from '@kula-ai/components';
import { mergeOptionsByValue } from '@/api/userSearch/useCcBccUserOptions';
import {
  toSelectOptions,
  resolveEmailUser,
  isSameEmailValue,
  type EmailUser,
  type EmailValueKey,
} from './emailValue';

// An empty template id is a real value, so "not applied yet" needs its own marker.
const NOT_APPLIED = Symbol('not-applied');

interface UseEmailComposerArgs<TPatch> {
  formik?: any;
  /** Seeds the composer from the surface's Formik values. Called once. */
  toValue: (_values: any) => EmailCompositionValue;
  /** Maps a snapshot back to the field names the surface's submit path reads. */
  toPatch: (
    _value: EmailCompositionValue,
    _resolveUser: (_value: string) => EmailUser | undefined
  ) => TPatch;
  /** Senders — the delegatable list, which also carries shared mailboxes. */
  fromUsers?: EmailUser[];
  /** Everyone selectable as cc/bcc. */
  ccBccUsers?: EmailUser[];
  /**
   * Recipients already saved on the record. The pickers only hold the page
   * of users they last fetched, so a saved recipient outside it rendered as
   * a bare id — merge them in so the label survives (ENG-3766).
   */
  savedUsers?: EmailUser[];
  valueKey?: EmailValueKey;
  /**
   * Called when the template row changes, before the patch is written. Return
   * extra Formik fields to merge — a template carries subject, sender, cc/bcc
   * and attachments on some surfaces.
   */
  onTemplateChange?: (
    _templateId: string | null,
    _previousId: string | null,
    _patch: TPatch
  ) => Record<string, unknown> | void;
  /**
   * Overrides how the patch reaches the form. Defaults to a `setValues` merge;
   * surfaces that write per key (`setFieldValue`) pass their own.
   */
  applyPatch?: (_patch: Record<string, unknown>) => void;
  /**
   * Re-seeds the snapshot from Formik whenever this changes — a surface whose
   * template picker lives outside the card uses it to pull in the new body.
   */
  seedKey?: unknown;
  /**
   * Adjusts an incoming snapshot before the guard runs — kula applies a picked
   * template's body itself, and some surfaces merge its subject on top.
   */
  normalise?: (
    _prev: EmailCompositionValue,
    _next: EmailCompositionValue
  ) => EmailCompositionValue;
  /**
   * Sender to adopt once it is known to be selectable. Both the stored sender
   * and the sender list resolve asynchronously, so this keeps re-checking and
   * is spent only on a real seed.
   */
  wantedFrom?: string;
  /**
   * Template whose subject and body are applied when its id changes. Keyed on
   * the id so a re-render never re-applies the same row over the user's edits.
   */
  selectedTemplate?: {
    id?: string | number;
    subject?: string;
    body?: string;
  } | null;
}

/**
 * The state machine every kula `Email` composer needs: a seeded snapshot, the
 * re-emit loop guard, and a merge (never a replace) back into Formik.
 *
 * Each surface used to hand-roll this. The copies drifted — several guards
 * compared only the fields that surface rendered, and one memoised its
 * extensions on unstable callbacks, which remounted the editor on every
 * keystroke and dropped the caret to the top of the document.
 */
export const useEmailComposer = <TPatch extends Record<string, unknown>>({
  formik,
  toValue,
  toPatch,
  fromUsers = [],
  ccBccUsers = [],
  savedUsers = [],
  valueKey = 'id',
  onTemplateChange,
  applyPatch,
  seedKey,
  normalise,
  wantedFrom,
  selectedTemplate,
}: UseEmailComposerArgs<TPatch>) => {
  const { values, setValues, submitCount } = formik ?? {};

  const fromOptions = useMemo(
    () =>
      mergeOptionsByValue(
        toSelectOptions(fromUsers, valueKey),
        toSelectOptions(savedUsers, valueKey)
      ),
    [fromUsers, savedUsers, valueKey]
  );
  const ccBccOptions = useMemo(
    () =>
      mergeOptionsByValue(
        toSelectOptions(ccBccUsers, valueKey),
        toSelectOptions(savedUsers, valueKey)
      ),
    [ccBccUsers, savedUsers, valueKey]
  );

  // Shared mailboxes need the whole option object — it carries `isSharedEmail`.
  const resolveUser = useCallback(
    (value: string) =>
      resolveEmailUser(
        value,
        [...fromUsers, ...ccBccUsers, ...savedUsers],
        valueKey
      ),
    [fromUsers, ccBccUsers, savedUsers, valueKey]
  );

  // Seed only — binding `value` straight to Formik would spin the form.
  const [value, setValue] = useState(() => toValue(values));
  const valueRef = useRef(value);

  // Re-seed during render, not in an effect, so the editor never paints a frame
  // holding the previous template's body. `FormBuilder` reinitialises Formik in
  // an effect, so on the render where `seedKey` first changes `values` is still
  // the previous template — hold the pending seed until they actually differ,
  // otherwise we latch the old body and never retry. A user edit cannot trip
  // this: `handleChange` has already moved `valueRef` to the typed content.
  const [seededFor, setSeededFor] = useState(seedKey);
  if (seededFor !== seedKey) {
    const reseeded = toValue(values);
    if (!isSameEmailValue(valueRef.current, reseeded)) {
      setSeededFor(seedKey);
      valueRef.current = reseeded;
      setValue(reseeded);
    }
  }

  // Latest mappers without rebuilding `handleChange` on every parent render.
  const mappers = useRef({ toPatch, onTemplateChange, applyPatch, normalise });
  mappers.current = { toPatch, onTemplateChange, applyPatch, normalise };

  // One write path, so a seeded value reaches the form exactly like a typed one.
  const commit = useCallback(
    (
      next: EmailCompositionValue,
      extra?: Record<string, unknown>,
      // `handleChange` has already mapped the patch to hand to
      // `onTemplateChange`; reuse it rather than mapping the same value twice.
      precomputed?: Record<string, unknown>
    ) => {
      valueRef.current = next;
      setValue(next);

      const merged = {
        ...(precomputed ?? mappers.current.toPatch(next, resolveUser)),
        ...(extra ?? {}),
      };
      if (mappers.current.applyPatch) {
        mappers.current.applyPatch(merged);
        return;
      }
      setValues((current: Record<string, unknown>) => ({
        ...current,
        ...merged,
      }));
    },
    [resolveUser, setValues]
  );

  const handleChange = useCallback(
    (incoming: EmailCompositionValue) => {
      const prev = valueRef.current;
      const next = mappers.current.normalise?.(prev, incoming) ?? incoming;
      const templateChanged = next.template !== prev.template;
      if (!templateChanged && isSameEmailValue(prev, next)) return;

      const patch = mappers.current.toPatch(next, resolveUser);
      const extra = templateChanged
        ? mappers.current.onTemplateChange?.(
            next.template ?? null,
            prev.template ?? null,
            patch
          )
        : undefined;

      commit(next, extra ?? undefined, patch);
    },
    [commit, resolveUser]
  );

  // Seed the sender once it is selectable — an unmatched address renders the
  // row empty and only fails at the backend.
  const seededFrom = useRef(false);
  useEffect(() => {
    if (seededFrom.current || !wantedFrom) return;
    seededFrom.current = true;
    // Commit even when the snapshot matches: that proves the CALLER seeded
    // the config, not that the form holds it — and submit reads the form
    // (ENG-3817).
    commit({ ...valueRef.current, from: wantedFrom });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedFrom]);

  // Apply a newly picked template's subject and body.
  const appliedTemplate = useRef<string | typeof NOT_APPLIED>(NOT_APPLIED);
  useEffect(() => {
    const key = String(selectedTemplate?.id ?? '');
    if (key === appliedTemplate.current) return;
    const initial = appliedTemplate.current === NOT_APPLIED;
    appliedTemplate.current = key;
    if (!selectedTemplate) return;

    // Already chosen at mount: adopt the id so the picker shows its name, but
    // keep the copy the form loaded with. Seeding from the current value the
    // way this ref used to left the row blank on every restored config.
    if (initial) {
      if (key && valueRef.current.template !== key) {
        // Local state only. Adopting the id is presentational — pushing it
        // through `commit` would write to the form on open and mark a pristine
        // one dirty, which surfaces gate their submit button on.
        const adopted = { ...valueRef.current, template: key };
        valueRef.current = adopted;
        setValue(adopted);
      }
      return;
    }

    commit({
      ...valueRef.current,
      template: key || null,
      subject: selectedTemplate.subject ?? '',
      content: selectedTemplate.body ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id]);

  return {
    value,
    setValue,
    valueRef,
    handleChange,
    fromOptions,
    ccBccOptions,
    resolveUser,
    submitAttempt: submitCount ?? 0,
  };
};
